﻿import React, { useState, useEffect } from 'react';
import { BarCodeParameters } from '../../models/Printing/bar-code-parameters';
import { AttachedImage } from '../../models/attached-image';
import { ModalMetadata } from '../../models/modal-metadata';
import { CrudFunctions } from '../../utilities/crud-functions';
import { BarCodeSymboloyTypes, ModalMessageType } from '../../models/enums';
import GlobalDialog from '../Modal/GlobalDialog';
import DmaFooter from '../DmaFooter';

import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import NumberBox from 'devextreme-react/number-box';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';

const barCodeTypes = [
    { id: BarCodeSymboloyTypes.Code128, name: "Code 128" },
    { id: BarCodeSymboloyTypes.Ean128, name: "GS1-128 (EAN-128)" },
    { id: BarCodeSymboloyTypes.DataMatrix, name: "ISO/IEC 16022 Data Matrix (ECC200)" },
    { id: BarCodeSymboloyTypes.QrCode, name: "QR Code" },
    { id: BarCodeSymboloyTypes.EpcQrCode, name: "EPC QR Code" },
    { id: BarCodeSymboloyTypes.MicroQrCode, name: "Micro QR Code" },
    { id: BarCodeSymboloyTypes.AztecCode, name: "Aztec Code" }
];

export default function BarCodeReaderDemo() {
    const [renderedBarCodeReading, setRenderedBarCodeReading] =
        useState<string>('');

    const [barCodeParameters, setBarCodeParameters] =
        useState<BarCodeParameters>();

    const [barCodeImage, setBarCodeImage] =
        useState<AttachedImage>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        beginListening();
        initSettings();
    }, []);

    let interval;
    let barCodeReading;

    return (
        <div className="pw-content">

            <div className="lq-title-bar pw-form-header">
                <div className="lq-main-title">Barcode Reader</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                </div>
            </div>

            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Use Bar Code Scanner</div>

            <div className="pw-margin-top-1">
                <div>Latest reading:</div>
                <div>{renderedBarCodeReading ? renderedBarCodeReading : 'NA'}</div>
            </div>

            <br /><br />
            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Generate Bar Code</div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-30">
                    <div className="pw-form-unit-label">Bar Code Types</div>
                    <SelectBox id="symbology" className="selectbox"
                        displayExpr='name' valueExpr='id'
                        dataSource={barCodeTypes ? barCodeTypes : []}
                        onValueChanged={onBarCodeParametersValueChanged}
                        value={barCodeParameters && barCodeParameters.symbology ?
                            barCodeParameters.symbology : null} />
                </div>
            </div>

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">Bar Code Text</div>
                <TextBox id="codeText"
                    value={barCodeParameters && barCodeParameters.codeText ?
                        barCodeParameters.codeText : undefined}
                    onValueChanged={onBarCodeParametersValueChanged} />
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit">
                    <CheckBox id="showCodeText" text="Show Text"
                        value={barCodeParameters && barCodeParameters.showCodeText ?
                            barCodeParameters.showCodeText : false}
                        onValueChanged={onBarCodeParametersValueChanged} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-12">
                    <div className="pw-form-unit-label">DPI X</div>
                    <NumberBox id="dpiX"
                        value={barCodeParameters && barCodeParameters.dpiX ?
                            barCodeParameters.dpiX : null}
                        onValueChanged={onBarCodeParametersValueChanged} />
                </div>

                <div className="pw-form-unit pw-comp-12">
                    <div className="pw-form-unit-label">DPI Y</div>
                    <NumberBox id="dpiY"
                        value={barCodeParameters && barCodeParameters.dpiY ?
                            barCodeParameters.dpiY : null}
                        onValueChanged={onBarCodeParametersValueChanged} />
                </div>
            </div>

            <div className="pw-margin-top-1">


                <div className="pw-margin-top-09">
                    <Button text="Create Bar Code" type="success"
                        className="pw-margin-left-05"
                        onClick={onCreateBarCodeClick} />
                </div>

            </div>

            {(barCodeImage && barCodeImage.base64Blob) &&
                <div className="pw-margin-top-1" style={{ width: "6rem" }}>
                    <img alt="svg molecular"
                        src={`data:image/png;base64, ${barCodeImage.base64Blob}`} />
                </div>
            }

            <DmaFooter />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );


    function onBarCodeParametersValueChanged(e: any) {
        let caller = e.element.id;

        setBarCodeParameters({ ...barCodeParameters, [caller]: e.value });
    }

    function beginListening() {

        document.addEventListener('keydown', function (e) {
            if (interval)
                clearInterval(interval);

            if (e.code === 'Enter') {
                setRenderedBarCodeReading(barCodeReading);

                barCodeReading = '';
                return;
            }
            else {
                console.log("KEY:");
                console.log(e.key);

                if (barCodeReading === undefined)
                    barCodeReading = '';

                if (e.key !== 'Shift')
                    barCodeReading += e.key;
            }

            interval = setInterval(() => barCodeReading = '', 20);
        });
    }

    async function onCreateBarCodeClick() {
        if (!barCodeParameters) {
            showInformationMessage(ModalMessageType.Information, "Missing Information", "You must first set the bar code parameters");
            return;
        }

        if (!barCodeParameters.symbology || !barCodeParameters.codeText) {
            showInformationMessage(ModalMessageType.Information, "Missing Information", "You must first set the bar code type and content (text)");
            return;
        }

        await CrudFunctions.postData("printing/barcode", barCodeParameters)
            .then((data) => {
                //console.log(data);
                setBarCodeImage(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Printing Bar Code");
            });
    }

    function initSettings() {
        setBarCodeParameters({ ...barCodeParameters, symbology: BarCodeSymboloyTypes.QrCode, showCodeText: false });
    }

    function showInformationMessage(mode: ModalMessageType, title: string, message: string) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}